import { Cancel } from '@mui/icons-material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Box,
  Button,
  Card,
  CardContent,
  colors,
  Dialog,
  Divider,
  IconButton,
  Modal,
  Portal,
  Stack,
  TextField,
  Typography,
  useTheme
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React from 'react';
import {
  useMutation,
  useQuery
} from 'react-fetching-library';
import { useForm } from 'react-hook-form';
import { Document, Page } from 'react-pdf';
import FormProvider from '../../../components/hook-form/FormProvider';
import {
  getDocumentToSignAction,
  getInstanceSignAgreementData,
  signDocumentAction
} from '../../../services/request';
import { useParams } from 'react-router-dom';
import { LoadingButton } from '@mui/lab';
import { MobilePDFReader } from 'react-read-pdf';
import { useResponsive } from '../../../services/hooks';
import { formatDateTime } from '../../../services/functions';

import { Worker, Viewer } from '@react-pdf-viewer/core';
import { zoomPlugin } from '@react-pdf-viewer/zoom';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/zoom/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: {
    xs: '90%',
    md: 400
  },
  maxHeight: '100vh'
};

function SignModal({ id, openModal, handleClose, setIsSigned }) {
  const { annex_nr, model } = useParams();
  const { loading, payload, error } = useQuery(
    getInstanceSignAgreementData(id, model)
  );
  const {
    loading: mloading,
    mutate,
    error: merror
  } = useMutation(signDocumentAction);
  const [employeeData, setEmployeeData] = React.useState({});

  React.useEffect(() => {
    if (!error && !loading) {
      setEmployeeData(payload);
    }
  }, [loading]);

  const methods = useForm({
    defaultValues: {
      full_name: '',
      annex_nr: annex_nr ? annex_nr : 0,
      model
    },
    mode: 'onSubmit'
  });

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = methods;

  const onSubmit = handleSubmit(async (data, e) => {
    e?.preventDefault();
    const { merror, payload: mpayload } = await mutate(data, id);
    if (!merror) {
      setIsSigned(true);
    } else {
      console.log('Document NOT SIGNED');
    }
  });


  const validateFullName = value => {
    // Usuń wszystkie spacje i normalizuj do małych liter
    const normalize = str => str.replace(/\s+/g, '').toLowerCase();

    const normalizedValue = normalize(value);
    const normalizedEmployeeName = normalize(employeeData.full_name);

    if (normalizedValue !== normalizedEmployeeName) {
      return 'Wprowadź poprawne nazwisko i imię, zgodne z podanymi wcześniej danymi.';
    }

    return true;
  };

  return (
    <Portal>
      <Modal open={openModal} onClose={handleClose}>
        <Card sx={style} className="overflow-auto">
          <CardContent
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexDirection: 'column',
              p: 7,
              position: 'relative'
            }}
          >
            <IconButton
              disabled={mloading}
              color="primary"
              onClick={handleClose}
              sx={{
                position: 'absolute',
                top: 0,
                right: 0
              }}
            >
              <Cancel fontSize="large" />
            </IconButton>

            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing={1}
              sx={{
                border: '1px solid lightblue',
                boxShadow: '0px 4px 8px rgba(0, 0, 255, 0.1)',
                borderRadius: '4px',
                backgroundColor: 'white',
                p: 1,
                mb: 1
              }}
            >
              <InfoOutlinedIcon sx={{ color: 'lightblue' }} />
              {employeeData.module_identifier && employeeData.module_identifier == 'zielone_szkoly' ? <Typography>
                Najpierw nazwisko, potem imię - muszą być identyczne, jak dane osoby reprezentującej szkołę w celu podpisania umowy
              </Typography>
                : <Typography align="center" variant="caption">
                Najpierw nazwisko, potem imię - muszą być identyczne, jak dane Klienta/Rodzica w umowie
                </Typography>}
            </Stack>
            <FormProvider
              methods={methods}
              onSubmit={onSubmit}
              className="flex flex-col gap-4"
            >
              <TextField
                label="Nazwisko i imię"
                sx={{ width: '100%', mb: 2 }}
                fullWidth
                {...register('full_name', {
                  required: 'To pole jest wymagane',
                  validate: validateFullName
                })}
                error={errors.full_name ? true : false}
                helperText={errors.full_name?.message}
              />
              <LoadingButton sx={{ textTransform: 'none' }} type="submit" variant="contained" disabled={mloading} loading={mloading} loadingPosition="start">
                {mloading ? <Typography variant="button">TWOJE DOKUMENTY SĄ WŁAŚNIE PODPISYWANE</Typography> : `Akceptuję ofertę zawarcia ${Number(annex_nr) ? 'aneksu' : 'umowy wraz z otrzymanymi załącznikami'}`}
              </LoadingButton>
            </FormProvider>
          </CardContent>
        </Card>
      </Modal>
    </Portal>
  );
}

export default function DocumentSignOrder({ id, setIsSigned, signingDueDate }) {
  const { annex_nr, model } = useParams();
  const theme = useTheme();
  const [showDocument, setShowDocument] = React.useState(true);
  const [numPages, setNumPages] = React.useState(0);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [agreementURL, setAgreementURL] = React.useState('');
  const [timeLeft, setTimeLeft] = React.useState((Number(annex_nr) ? 1 : 1) * 60 * 1000);
  // const [timeLeft, setTimeLeft] = React.useState(10 * 1000);
  const [openModal, setOpenModal] = React.useState(false);
  const isDesktop = useResponsive('up', 'lg');
  const [heightAdapted, setHeightAdapted] = React.useState(false);

  const [scale, setScale] = React.useState(1.0);
  const zoomIn = () => setScale(prevScale => prevScale + 0.2); // Przybliżenie
  const zoomOut = () => setScale(prevScale => Math.max(prevScale - 0.2, 0.5)); // Oddalenie (nie mniej niż 0.5)
  const zoomPluginInstance = zoomPlugin();

  const template_name = Number(annex_nr) ? 'annex' : 'agreement';
  const fileName = 'Umowa';

  const { loading, payload, error } = useQuery(
    getDocumentToSignAction(id, template_name, fileName, model, annex_nr)
  );

  React.useEffect(() => {
    if (!error && !loading) {
      setAgreementURL(window.URL.createObjectURL(payload));
    }
  }, [loading]);

  React.useEffect(() => {
    const intervalId = setInterval(() => {
      // setTimeLeft(prevTimeLeft => prevTimeLeft > 0 ? prevTimeLeft - 10 * 1000 : 0);
      setTimeLeft(prevTimeLeft => prevTimeLeft > 0 ? prevTimeLeft - 60 * 1000 : 0);
    }, 60 * 1000);
    // }, 10 * 1000);

    return () => clearInterval(intervalId);
  }, [timeLeft]);

  const handleClose = () => {
    setOpenModal(false);
  };

  const handlePDFLoadSuccess = ({ doc }) => {
    // eslint-disable-next-line no-underscore-dangle
    setNumPages(doc._pdfInfo.numPages);
  };

  const handlePDFLoadSuccessDesktop = ({ numPages }) => {
    setNumPages(numPages);
  };

  // React.useEffect(() => {
  //   if (isDesktop) {
  //     return;
  //   }
  //   const handlePageChange = evt => {
  //     setCurrentPage(evt.pageNumber);
  //   };
  //   document.addEventListener('pagechange', handlePageChange);
  // }, [isDesktop]);

  // React.useEffect(() => {
  //   const pages = document.querySelectorAll('.page');
  //   if (pages.length == 0) {
  //     return;
  //   }
  //   const maxPageElement = Array.from(pages).reduce((maxElement, currentElement) => {
  //     const maxPageNumber = parseInt(maxElement.getAttribute('data-page-number') || '0', 10);
  //     const currentPageNumber = parseInt(currentElement.getAttribute('data-page-number') || '0', 10);

  //     return currentPageNumber > maxPageNumber ? currentElement : maxElement;
  //   }, pages[0]);
  //   setNumPages(maxPageElement.getAttribute('data-page-number'));
  // }, [document.querySelectorAll('.page').length]);

  // React.useEffect(() => {
  //   setTimeout(() => {
  //     const pageElements = document.querySelectorAll('.page[data-page-number]');
  //     if (pageElements) {
  //       pageElements.forEach(pageElement => {
  //         pageElement.style.overflow = 'hidden';
  //         pageElement.style.margin = 'auto';
  //         if (!heightAdapted) {
  //           const intHeight = parseInt(pageElement.style.height, 10);
  //           const newHeight = intHeight + 3;
  //           const newPxHeight = `${newHeight}px`;
  //           pageElement.style.height = newPxHeight;
  //           setHeightAdapted(true);
  //         }
  //       });
  //     }
  //   }, 100);
  // }, [document.querySelectorAll('.page[data-page-number]')]);

  const renderToolbar = Toolbar => (
    <Toolbar>
      {slots => {
        const {
          ZoomIn,
          ZoomOut,
          CurrentScale,
          CurrentPageLabel,
          NumberOfPages

        } = slots;

        return (
          <Stack
            backgroundColor={theme.palette.primary.main}
            direction="row" alignItems="center"
            justifyContent="space-between"
            spacing={1} width="100%" position="sticky"
            height="5vh"
          >
            <Stack direction="row" alignItems="center">
              <Box>
                <ZoomOut>
                  { props => (
                    <IconButton onClick={props.onClick} color="secondary" size="small">
                      <ZoomOutIcon />
                    </IconButton>
                  )
                  }
                </ZoomOut>
              </Box>
              <Box>
                <CurrentScale>{props => <Typography align="center" color="white" variant="caption2">
                  {`${Math.round(props.scale * 100)}%`}
                </Typography>}
                </CurrentScale>
              </Box>
              <Box>
                <ZoomIn>
                  { props => (
                    <IconButton onClick={props.onClick} color="secondary" size="small">
                      <ZoomInIcon />
                    </IconButton>
                  )
                  }
                </ZoomIn>
              </Box>
            </Stack>
            <Typography color="white" variant="caption2">
                Podpisz Umowę do: <u>{formatDateTime(signingDueDate, true)}</u>
            </Typography>
            <Stack direction="row">
              <CurrentPageLabel>
                {props => {
                  setCurrentPage(props.currentPage + 1);

                  return <Typography variant="caption2" mr={2}>{props.currentPage + 1} / <NumberOfPages /></Typography>;
                }}
              </CurrentPageLabel>
            </Stack>
          </Stack>
        );
      }}
    </Toolbar>
  );

  const defaultLayoutPluginInstance = defaultLayoutPlugin({
    renderToolbar,
    sidebarTabs: defaultTabs => []
  });

  return (
    <>
      {showDocument && (
        isDesktop
          ? <Dialog
            open={showDocument}
            fullScreen
          >
            <Box
              sx={{
                backgroundColor: theme.palette.primary.main,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                p: 1
              }}
            >
              {signingDueDate && (
                <Typography align="center" color="white" variant="h6">
                Podpisz Umowę do: <u>{formatDateTime(signingDueDate, true)}</u>
                </Typography>
              )}
            </Box>
            <Divider />
            <Stack
              flexDirection="row"
              justifyContent="center"
              alignItems="center"
              sx={{
                backgroundColor: theme.palette.grey[200],
                py: 2,
                px: 4
              }}
            >
              <Stack direction="column" justifyContent="space-between" height="100%" alignItems="right">
                <Stack
                  direction="row"
                  spacing={2}
                  boxShadow={5}
                  sx={{
                    alignItems: 'center',
                    backgroundColor: colors.green[50],
                    borderRadius: 4,
                    mr: 1
                  }}
                >
                  <IconButton onClick={zoomOut} color="primary" size="large">
                    <Typography variant="h4">-</Typography>
                  </IconButton>

                  <Typography variant="h6" color="textPrimary">
                    {(scale * 100).toFixed(0)}%
                  </Typography>

                  <IconButton onClick={zoomIn} color="primary" size="large">
                    <Typography variant="h5">+</Typography>
                  </IconButton>
                </Stack>
                {currentPage !== 1 && (
                  <IconButton
                    onClick={() => setCurrentPage(currentPage - 1)}
                    color="primary"
                    size="large"
                  >
                    <ArrowBackIcon />
                  </IconButton>
                )}
                <Box width={1} height={24} />
              </Stack>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  boxShadow: 3,
                  borderRadius: 2,
                  overflow: 'hidden'
                }}
              >
                <Document file={agreementURL} onLoadSuccess={handlePDFLoadSuccessDesktop}>
                  <Page pageNumber={currentPage} scale={scale} />
                </Document>
              </Box>
              <Stack direction="column" justifyContent="space-between" height="100%" alignItems="right">
                <Box width={160} height={50} />
                {currentPage !== numPages && (
                  <IconButton
                    onClick={() => setCurrentPage(currentPage + 1)}
                    color="primary"
                    size="large"
                  >
                    <ArrowForwardIcon />
                  </IconButton>
                )}
                <Box width={1} />

              </Stack>
            </Stack>

            <Button
              type="submit"
              sx={{
                textTransform: 'none',
                position: 'fixed',
                bottom: 0,
                left: 0,
                width: '100%',
                borderRadius: 0,
                zIndex: 1300,
                p: 1,
                backgroundColor: timeLeft > 0 ? 'primary.main' : 'primary.main',
                '&.Mui-disabled': {
                  backgroundColor: 'primary.main',
                  color: '#fff'
                }
              }}
              onClick={() => setOpenModal(true)}
              disabled={timeLeft > 0}
              variant="contained"
            >
              {timeLeft > 0
                ? <Typography variant="h6">Przycisk aktywny po upływie {Math.ceil(timeLeft / 60000)} min</Typography>
                : Number(annex_nr) ? <Typography variant="h6">KLIKNIJ TUTAJ I ZAAKCEPTUJ ANEKS</Typography> : <Typography variant="h6">KLIKNIJ TUTAJ I ZAAKCEPTUJ UMOWĘ</Typography>}
            </Button>
          </Dialog>

          // MOBILKA
          : <Box width="100%">
            {agreementURL && (
              <Worker workerUrl={'https://unpkg.com/pdfjs-dist@2.5.207/build/pdf.worker.min.js'}>
                <Box
                  height="calc(100vh - 50px)" // Dostosuj wysokość, aby toolbar był widoczny
                  overflow="auto" // Włącza przewijanie wewnątrz kontenera
                >
                  <Viewer fileUrl={agreementURL} // Zmienna kontrolująca aktualną stronę
                    onDocumentLoad={handlePDFLoadSuccess}
                    plugins={[zoomPluginInstance, defaultLayoutPluginInstance]}
                  />
                </Box>
              </Worker>
            )}

            {/* Przycisk akceptacji */}
            <Button
              type="submit"
              sx={{
                textTransform: 'none',
                position: 'fixed',
                bottom: 0,
                left: 0,
                width: '100%',
                borderRadius: 0,
                zIndex: 1300,
                p: 1,
                backgroundColor: timeLeft > 0 ? 'primary.main' : 'primary.main',
                '&.Mui-disabled': {
                  backgroundColor: 'primary.main',
                  color: '#fff'
                }
              }}
              onClick={() => setOpenModal(true)}
              disabled={timeLeft > 0}
              variant="contained"
            >
              {timeLeft > 0
                ? <Typography variant="body1">Przycisk aktywny po upływie {Math.ceil(timeLeft / 60000)} min</Typography>
                : Number(annex_nr) ? <Typography variant="body1">KLIKNIJ TUTAJ I ZAAKCEPTUJ ANEKS</Typography> : <Typography variant="h6">KLIKNIJ TUTAJ I ZAAKCEPTUJ UMOWĘ</Typography>}
            </Button>
          </Box>
      )}
      <SignModal
        openModal={openModal}
        handleClose={handleClose}
        id={id}
        setIsSigned={setIsSigned}
      />
    </>
  );
}
